import {GroupWidget, Variable, Widget} from "../internalwidgets";
import {GridStack} from "gridstack";
import React, {Dispatch, SetStateAction, useEffect} from "react";
import {v4 as uuidv4} from "uuid";
import {cn, usePreserveQueryParamsNavigate} from "../../ui/lib/utils";
import {cloneWidget, recursiveSave, removeWidget} from "../utils";
import {determineFullWidth, findParentGrid, getVariableResolvedValuesAtGroup} from "../utils/grid";
import {WidgetDiv} from "./WidgetDiv";
import {CopyIcon, VariableIcon, XIcon} from "lucide-react";
import {Button} from "../../ui/button";
import {MdOutlineAddchart, MdOutlineUnfoldLess, MdOutlineUnfoldMore} from "react-icons/md";
import {Drawer} from "vaul";
import {AddNewWidgetDrawer} from "../AddNewWidgetDrawer";
import {Input} from "../../ui/input";
import {Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle} from "../../ui/dialog";
import axios from "../../../utility/customAxios";
import {useSearchParams} from "react-router-dom";
import {ButtonWithTooltip, RuntimeVariable} from "../Dashboard";
import {VariablesDrawer} from "./VariablesDrawer";
import {GroupVariableManager} from "./GroupVariableManager";
import {substituteVariablesInText} from "../../Charts/utils";

export const Group = (props: {
    stylesheet?: React.CSSProperties,
    editable: boolean,
    editButton?: React.ReactElement,
    saveButton?: React.ReactElement,
    cloneDashboardButton?: React.ReactElement,
    setEdit?: Dispatch<SetStateAction<boolean>>,
    fullScreen?: boolean,
    disallowDelete?: boolean,
    className?: string,
    id?: string,
    variables?: Variable[],
    runtimeVariables: Map<string, RuntimeVariable[]>,
    setRuntimeVariables: Dispatch<SetStateAction<Map<string, RuntimeVariable[]>>>,
    widget: GroupWidget,
    grids: Map<string, GridStack>,
    setGrids: Dispatch<SetStateAction<Map<string, GridStack>>>,
    widgets: Map<string, Widget>,
    setWidgets: Dispatch<SetStateAction<Map<string, Widget>>>,
    updateParentOfMount?: (id: string) => void,
    ref?: React.MutableRefObject<any>,
    setRootChildren?: Dispatch<SetStateAction<Widget[]>>
}) => {
    const [id] = React.useState<string>(props.id ?? "id-" + uuidv4())
    const [gridStack, setGridStack] = React.useState<GridStack>()
    const [children, setChildren] = React.useState<Widget[]>(props.widget.children)
    const [isAddWidgetOpen, setIsAddWidgetOpen] = React.useState<boolean>(false)
    const [isVariablesOpen, setIsVariablesOpen] = React.useState<boolean>(false)
    const [title, setTitle] = React.useState<string>(props.widget.title ?? "Group")
    const [deleteDashboardDialogOpen, setDeleteDashboardDialogOpen] = React.useState<boolean>(false)
    const [variables, setVariables] = React.useState<Variable[]>(props.variables ?? [])
    const [searchParams] = useSearchParams();
    const navigate = usePreserveQueryParamsNavigate();
    const [isFullWidth, setIsFullWidth] = React.useState(false);
    const [isFolded, setIsFolded] = React.useState(false);

    // Add state for resolved variables
    const [resolvedVariables, setResolvedVariables] = React.useState<RuntimeVariable[]>(() => {
        return getVariableResolvedValuesAtGroup(id, props.grids, props.runtimeVariables);
    });

    useEffect(() => {
        if (props.editable) {
            setIsFolded(false)
        }
    }, [props.editable])

    // Update resolved variables when runtime variables change
    useEffect(() => {
        setResolvedVariables(getVariableResolvedValuesAtGroup(id, props.grids, props.runtimeVariables));
    }, [props.runtimeVariables, props.grids, id]);

    // Keep variables in sync with widget's variables
    useEffect(() => {
        setVariables(props.widget.variables ?? []);
    }, [props.widget.variables]);

    // Set runtime variables to the default values when instantiated
    useEffect(() => {
        props.setRuntimeVariables((prev) => {
            let newRuntimeVariables = new Map<string, RuntimeVariable[]>(prev)
            let newGroupVariables: RuntimeVariable[] = []
            for (const variable of props.variables ?? []) {
                newGroupVariables.push({name: variable.name, key: variable.key, value: variable.defaultValue})
            }
            newRuntimeVariables.set(id, newGroupVariables)
            return newRuntimeVariables
        })
    }, [props.variables]);

    useEffect(() => {
        let gridId = "grid-" + id
        let gStack = GridStack.init({
            draggable: {
                handle: '.draggablehandle'
            },
            animate: true,
            resizable: {
                handles: 's,e,w,se,sw'
            },
            subGridDynamic: true,
            acceptWidgets: true,
            float: true,
            subGridOpts: {
                column: 'auto',
            },
            column: "auto",
            cellHeight: "128px",
        }, "." + gridId);

        if (!props.editable) {
            gStack.setStatic(true)
        }

        setGridStack(gStack)

        props.setGrids((prev) => {
            let newGrids = new Map<string, GridStack>(prev)
            newGrids.set(gridId, gStack)
            return newGrids
        })

        props.setWidgets((prev) => {
            let newWidgets = new Map<string, Widget>(prev)
            newWidgets.set(id, props.widget)
            return newWidgets
        })

        if (props.updateParentOfMount !== undefined) {
            props.updateParentOfMount(id)
        }
    }, [])

    useEffect(() => {
        let {element, grid, gridWidgetId} = findParentGrid(id, props.grids)
        grid?.batchUpdate(true)
        // If we're folding, we need to make ourselves smaller first, then pull everything else up
        if (isFolded) {
            if (grid) {
                // We need to update the gridstack widget height
                grid.update(id, {
                    h: 1,
                });
            }
        }
        if (props.widget.position?.w == 12) {
            if (grid) {
                if (isFolded) {
                    const widgets = grid.getGridItems().sort((x, y) => x.gridstackNode?.y! - y.gridstackNode?.y!);
                    let currentY = grid.getGridItems().find(w => w.id === id)?.gridstackNode?.y ?? 0;
                    for (let i = 0; i < widgets.length; i++) {
                        const widget = widgets[i];
                        if (widget.gridstackNode?.y! > currentY) {
                            grid.update(widget.id, {y: widget.gridstackNode?.y! - (props.widget.position.h! - 1)});
                        }
                    }
                } else {
                    // Update the y axis of everything after this widget
                    const widgets = grid.getGridItems().sort((x, y) => x.gridstackNode?.y! - y.gridstackNode?.y!);
                    let currentY = grid.getGridItems().find(w => w.id === id)?.gridstackNode?.y ?? 0;
                    for (let i = 0; i < widgets.length; i++) {
                        const widget = widgets[i];
                        if (widget.gridstackNode?.y! > currentY) {
                            grid.update(widget.id, {y: widget.gridstackNode?.y! + (props.widget.position.h! - 1)});
                        }
                    }
                }
            }
        }

        // If we're unfolding, we first need to move everything else down, then make ourselves bigger
        if (!isFolded) {
            if (grid) {
                // We need to update the gridstack widget height
                grid.update(id, {
                    h: props.widget.position?.h ?? 2,
                });
            }
        }
        grid?.batchUpdate(false)
    }, [isFolded]);

    useEffect(() => {
        const {element, grid} = findParentGrid(id, props.grids);
        const fullWidth = determineFullWidth(element, grid, props.widget.position ?? {});
        setIsFullWidth(fullWidth);
    }, [id, props.grids, findParentGrid, determineFullWidth, props.widget.position]);

    let leftMargin = "pl-1"
    if (props.widget.position?.x === 0) {
        leftMargin = "pl-2"
    }
    let rightMargin = "pr-1"
    if (isFullWidth) {
        rightMargin = "pr-2"
    }
    let topMargin = "pt-1"
    if (props.widget.position?.y === 0) {
        topMargin = "pt-2"
    }
    let bottomMargin = "pb-1"

    let isRoot = props.id === "id-root";

    let groupHeight = "h-12";
    let titleHeight = "h-8";
    let rootStyles = ""

    if (isRoot) {
        leftMargin = "pl-0"
        rightMargin = "pr-0"
        topMargin = "pt-0"
        bottomMargin = "pb-0"
        rootStyles = "border-0 rounded-none"
        groupHeight = "h-8";
        titleHeight = "h-6";
    }

    return (
        <div id={id} className={cn(id, "h-full")}>
            <div className={cn("flex flex-col h-full", props.className)}>
                <div className={cn("flex grow w-full h-full", leftMargin, topMargin, rightMargin, bottomMargin)}>
                    <div className={cn("flex flex-col grow shrink rounded w-full h-full border", rootStyles)}>
                        <div
                            className={cn("flex border-b draggablehandle justify-between pl-2 gap-4 bg-backgroundlight relative", groupHeight)}>
                            {/* Left section */}
                            <div className="flex items-center">
                                {!props.editable && <GroupVariableManager widgetId={id} variables={variables}
                                                                          runtimeVariables={props.runtimeVariables}
                                                                          setRuntimeVariables={props.setRuntimeVariables}/>}
                            </div>

                            {/* Center section - title */}
                            <div className="flex-1 flex items-center justify-center min-w-0 px-2">
                                <div
                                    className="flex text-textmedium text-center items-center justify-center truncate text-lg font-semibold overflow-y-hidden max-w-full">
                                    {
                                        props.editable &&
                                        <div className={"flex flex-col justify-center w-full"}>
                                            <Input
                                                className={cn("px-1 w-full", titleHeight)}
                                                value={title}
                                                onChange={(e) => {
                                                    props.setWidgets((prev) => {
                                                        let newWidgets = new Map<string, Widget>(prev)
                                                        let newWidget = {
                                                            ...props.widget,
                                                            title: e.target.value
                                                        }
                                                        setTitle(e.target.value)
                                                        newWidgets.set(id, newWidget)
                                                        return newWidgets
                                                    })
                                                }}/>
                                        </div>
                                    }
                                    {
                                        !props.editable && !isRoot &&
                                        <span className="truncate">
                                            {substituteVariablesInText(title, resolvedVariables)}
                                        </span>
                                    }
                                </div>
                            </div>

                            {/* Right section - action buttons */}
                            <div className="flex items-center gap-1">
                                <div
                                    className={cn("flex flex-none gap-1", (props.editButton === undefined && !props.editable) && "hidden")}>
                                    {
                                        // Duplicate
                                        props.editable && props.setRootChildren &&
                                        <div className="flex flex-col justify-center">
                                            <ButtonWithTooltip
                                                icon={CopyIcon}
                                                tooltipText="Duplicate widget"
                                                onClick={() => {
                                                    let widget = recursiveSave(id, props.widgets, props.grids);
                                                    cloneWidget(widget, props.setRootChildren!)
                                                }}
                                            />
                                        </div>
                                    }
                                    {props.editable &&
                                        <div className="flex flex-col justify-center">
                                            <Drawer.Root direction="right" open={isAddWidgetOpen}>
                                                <ButtonWithTooltip
                                                    icon={MdOutlineAddchart}
                                                    tooltipText="Add widget"
                                                    onClick={() => setIsAddWidgetOpen(true)}
                                                />
                                                <Drawer.Portal>
                                                    <Drawer.Content
                                                        data-vaul-no-drag
                                                        draggable={false}
                                                        onAbort={() => setIsAddWidgetOpen(false)}
                                                        onDragEnd={() => setIsAddWidgetOpen(false)}
                                                        onInteractOutside={() => setIsAddWidgetOpen(false)}
                                                        className="bg-backgrounddark flex border-l flex-col h-full w-1/3 mt-24 fixed bottom-0 right-0 z-50">
                                                        <AddNewWidgetDrawer
                                                            id={id}
                                                            setRuntimeVariables={props.setRuntimeVariables}
                                                            widgets={props.widgets}
                                                            setWidgets={props.setWidgets}
                                                            setIsOpen={setIsAddWidgetOpen}
                                                            setVariables={setVariables}
                                                            setChildren={setChildren}/>
                                                    </Drawer.Content>
                                                </Drawer.Portal>
                                            </Drawer.Root>
                                        </div>
                                    }
                                    {props.editable &&
                                        <div className="flex flex-col justify-center">
                                            <Drawer.Root modal={false} direction="right" open={isVariablesOpen}>
                                                <ButtonWithTooltip
                                                    icon={VariableIcon}
                                                    tooltipText="Manage variables"
                                                    onClick={() => setIsVariablesOpen(true)}
                                                />
                                                <Drawer.Portal>
                                                    <Drawer.Content
                                                        data-vaul-no-drag
                                                        draggable={false}
                                                        onAbort={() => setIsVariablesOpen(false)}
                                                        onDragEnd={() => setIsVariablesOpen(false)}
                                                        onInteractOutside={() => setIsVariablesOpen(false)}
                                                        className="bg-backgrounddark flex border-l flex-col h-full w-[80%] mt-24 fixed bottom-0 right-0 z-50">
                                                        <VariablesDrawer
                                                            onClose={() => setIsVariablesOpen(false)}
                                                            variables={variables}
                                                            onAddVariable={(variable) => {
                                                                props.setWidgets((prev) => {
                                                                    let newWidgets = new Map<string, Widget>(prev);
                                                                    let currentWidget = prev.get(id) as GroupWidget;
                                                                    let currentVariables = currentWidget?.variables || [];
                                                                    let newWidget = {
                                                                        ...props.widget,
                                                                        variables: [...currentVariables, variable]
                                                                    };
                                                                    newWidgets.set(id, newWidget);
                                                                    return newWidgets;
                                                                });
                                                                setVariables(prevVariables => [...prevVariables, variable]);
                                                            }}
                                                            onEditVariable={(index, variable) => {
                                                                props.setWidgets((prev) => {
                                                                    let newWidgets = new Map<string, Widget>(prev);
                                                                    let currentWidget = prev.get(id) as GroupWidget;
                                                                    let currentVariables = [...(currentWidget?.variables || [])];
                                                                    currentVariables[index] = variable;
                                                                    let newWidget = {
                                                                        ...props.widget,
                                                                        variables: currentVariables
                                                                    };
                                                                    newWidgets.set(id, newWidget);
                                                                    return newWidgets;
                                                                });

                                                                // Update runtime variables to reflect the name change
                                                                if (props.runtimeVariables && props.setRuntimeVariables) {
                                                                    props.setRuntimeVariables(prev => {
                                                                        const newMap = new Map(prev);
                                                                        const currentVars = newMap.get(id) ?? [];
                                                                        const updatedVars = currentVars.map((v, i) => {
                                                                            if (i === index) {
                                                                                return {
                                                                                    name: variable.name,
                                                                                    key: variable.key,
                                                                                    value: v.value
                                                                                };
                                                                            }
                                                                            return v;
                                                                        });
                                                                        newMap.set(id, updatedVars);
                                                                        return newMap;
                                                                    });
                                                                }

                                                                setVariables(prevVariables => {
                                                                    const newVariables = [...prevVariables];
                                                                    newVariables[index] = variable;
                                                                    return newVariables;
                                                                });
                                                            }}
                                                            onDeleteVariable={(index) => {
                                                                props.setWidgets((prev) => {
                                                                    let newWidgets = new Map<string, Widget>(prev);
                                                                    let currentWidget = prev.get(id) as GroupWidget;
                                                                    let currentVariables = [...(currentWidget?.variables || [])];
                                                                    currentVariables.splice(index, 1);
                                                                    let newWidget = {
                                                                        ...props.widget,
                                                                        variables: currentVariables
                                                                    };
                                                                    newWidgets.set(id, newWidget);
                                                                    return newWidgets;
                                                                });

                                                                // Update runtime variables
                                                                if (props.runtimeVariables && props.setRuntimeVariables) {
                                                                    props.setRuntimeVariables(prev => {
                                                                        const newMap = new Map(prev);
                                                                        const currentVars = newMap.get(id) ?? [];
                                                                        const updatedVars = currentVars.filter((_, i) => i !== index);
                                                                        newMap.set(id, updatedVars);
                                                                        return newMap;
                                                                    });
                                                                }

                                                                setVariables(prevVariables => {
                                                                    const newVariables = [...prevVariables];
                                                                    newVariables.splice(index, 1);
                                                                    return newVariables;
                                                                });
                                                            }}
                                                            widgetId={id}
                                                            runtimeVariables={props.runtimeVariables}
                                                            setRuntimeVariables={props.setRuntimeVariables}
                                                        />
                                                    </Drawer.Content>
                                                </Drawer.Portal>
                                            </Drawer.Root>
                                        </div>
                                    }
                                    {!props.disallowDelete && props.editable &&
                                        <div
                                            className={"flex flex-col justify-center items-center text-center grow shrink"}>
                                            <Dialog open={deleteDashboardDialogOpen}>
                                                <DialogContent className={"w-1/3 text-textmedium"}
                                                               onInteractOutside={() => setDeleteDashboardDialogOpen(false)}>
                                                    <div className={"flex flex-row justify-between items-center grow"}>
                                                        <div className={"flex flex-col justify-center"}>
                                                            <DialogTitle className={"text-textmedium"}>Are you
                                                                sure?</DialogTitle>
                                                        </div>
                                                        <div className={"flex flex-col justify-center"}>
                                                            <DialogClose asChild
                                                                         onClick={() => setDeleteDashboardDialogOpen(false)}>
                                                                <XIcon
                                                                    className={"text-textdark hover:cursor-pointer hover:text-primary"}/>
                                                            </DialogClose>
                                                        </div>
                                                    </div>
                                                    <div className={"flex flex-col"}>
                                                        <div className={"flex flex-col mt-4"}>
                                                            <DialogDescription>
                                                                Are you sure you want to delete this dashboard? This
                                                                action is irreversible.
                                                            </DialogDescription>
                                                        </div>
                                                        <div className={"flex flex-col mt-8"}>
                                                            <Button
                                                                className={"h-6 px-2 py-4 bg-red-500/10 border-red-500 rounder border text-textmedium"}
                                                                onClick={() => {
                                                                    axios.delete(`/api/v1/dashboard?dashboardId=${searchParams.get("dashboardId")}`).then(() => {
                                                                        navigate("/dashboards")
                                                                    }).catch((e) => {
                                                                        console.error(e)
                                                                    })
                                                                }}>
                                                                <div className={"flex"}>
                                                                    <div className={"flex flex-col justify-center"}>
                                                                        <XIcon className={"h-4 text-textmedium"}/>
                                                                    </div>
                                                                    <div
                                                                        className={"flex flex-col justify-center"}>Delete
                                                                    </div>
                                                                </div>
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </DialogContent>
                                            </Dialog>
                                            <ButtonWithTooltip
                                                icon={XIcon}
                                                tooltipText="Delete widget"
                                                onClick={() => {
                                                    if (id === "id-root") {
                                                        setDeleteDashboardDialogOpen(true)
                                                        return
                                                    }
                                                    removeWidget(id, props.grids, props.setGrids, props.widgets, props.setWidgets);
                                                }}
                                            />
                                        </div>
                                    }
                                    {
                                        props.cloneDashboardButton !== undefined && props.editable &&
                                        <div className={"flex flex-col justify-center items-center text-center"}>
                                            {props.cloneDashboardButton}
                                        </div>
                                    }
                                    {
                                        props.saveButton !== undefined && props.editable &&
                                        <div
                                            className={"flex flex-col justify-center items-center text-center"}>
                                            {props.saveButton}
                                        </div>
                                    }
                                    {
                                        props.editButton !== undefined && !props.editable &&
                                        <div
                                            className={"flex flex-col justify-center items-center text-center"}>
                                            {props.editButton}
                                        </div>
                                    }
                                </div>
                                {
                                    !isRoot && !props.editable &&
                                    <div
                                        className={"flex font-semibold flex-col justify-center items-center text-center hover:text-secondary hover:cursor-pointer text-textmedium bg-backgroundlight z-10 px-2"}
                                        onClick={() => {
                                            const {element, grid} = findParentGrid(id, props.grids);
                                            if (grid) {
                                                setIsFolded((folded) => !folded);
                                            }
                                        }}
                                    >
                                        {isFolded && <MdOutlineUnfoldMore className={"w-4 h-4"}/>}
                                        {!isFolded && <MdOutlineUnfoldLess className={"w-4 h-4"}/>}
                                    </div>
                                }
                            </div>
                        </div>
                        <div
                            className={cn("grow shrink flex flex-col justify-center items-center", isFolded ? "flex" : "hidden")}>
                            <div className="flex grow shrink items-center justify-center p-2 text-lg font-semibold">
                                Group is folded, unfold to see {children.length} widgets.
                            </div>
                        </div>
                        <div className={cn("w-full h-full overflow-auto", isFolded ? "hidden" : "block")}>
                            <div className={cn("w-full min-h-full grow shrink grid-" + id)} style={props.stylesheet}>
                                {
                                    gridStack !== undefined && children.length > 0 &&
                                    children.map((child, i) =>
                                        WidgetDiv({
                                            stylesheet: props.stylesheet ?? {},
                                            setRootChildren: props.setRootChildren ?? setChildren,
                                            editable: props.editable,
                                            widget: child,
                                            parentGridStack: gridStack,
                                            grids: props.grids,
                                            setGrids: props.setGrids,
                                            widgets: props.widgets,
                                            setWidgets: props.setWidgets,
                                            runtimeVariables: props.runtimeVariables,
                                            setRuntimeVariables: props.setRuntimeVariables
                                        })
                                    )
                                }
                                {
                                    gridStack !== undefined && children.length === 0 && id === "id-root" &&
                                    <div className="flex flex-col items-start justify-start h-full p-4">
                                        <button
                                            onClick={() => {
                                                // Enable edit mode using the prop
                                                if (props.setEdit) {
                                                    props.setEdit(true);
                                                }
                                                // Open the widget drawer
                                                setIsAddWidgetOpen(true);
                                            }}
                                            className="flex flex-col items-center justify-center gap-2 p-8 rounded-lg border border-primary bg-primarytransparent hover:text-white"
                                        >
                                            <div className="text-6xl font-light">+</div>
                                            <div className="text-sm">Add a widget or use a template</div>
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}